import { Typography } from '@mui/material';
import { ReactNode } from 'react';

import { PieceAction } from '@apiContract';

import { clearRecipientFilter } from '@redux/slices/recipientFilter';
import { useDispatch, useSelector } from '@redux/store';

import { useFinishPieceMutation } from '../../hooks/useFinishPieceMutation';
import { ActionComponentProps } from '../../index';
import { getItemsCount } from '../../utils';
import { DialogQrCodeConfirmation } from '../DialogQrCodeConfirmation';
import { ActionWithInfo } from './ActionWithInfo';

type FinishWithQrCodeAction = PieceAction.FinishDeliveryWithQrCode | PieceAction.FinishPickUpWithQrCode;

type FinishWithQrCodeConfig = {
  title: { single: string; plural: string };
  prefilledInfo: { single: string; plural: (count: number) => ReactNode };
};

const QR_CODE_ACTION_CONFIG: Record<FinishWithQrCodeAction, FinishWithQrCodeConfig> = {
  [PieceAction.FinishDeliveryWithQrCode]: {
    title: { single: 'Deliver item', plural: 'Deliver selected items' },
    prefilledInfo: {
      single: "Please deliver this item to the recipient, and click Complete when you're done.",
      plural: (count) => (
        <>
          Please deliver <strong>{count}</strong> items to the recipient, and click Complete when you&apos;re done.
        </>
      ),
    },
  },
  [PieceAction.FinishPickUpWithQrCode]: {
    title: { single: 'Complete pickup', plural: 'Complete pickup for selected items' },
    prefilledInfo: {
      single: "Please give this item to the recipient, and click Complete when you're done.",
      plural: (count) => (
        <>
          Please give <strong>{count}</strong> items to the recipient, and click Complete when you&apos;re done.
        </>
      ),
    },
  },
};

type FinishWithQrCodeProps = ActionComponentProps & {
  action: FinishWithQrCodeAction;
};

export const FinishWithQrCode = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  validationData,
  action,
}: FinishWithQrCodeProps) => {
  const dispatch = useDispatch();
  const { runAction, isLoading } = useFinishPieceMutation();

  const { qrCode: prefilledQrCode } = useSelector((state) => state.recipientFilter);

  const count = getItemsCount(mailpieces);
  const { title, prefilledInfo } = QR_CODE_ACTION_CONFIG[action];

  const confirmAction = async (
    qrCodeData: string,
    skipNotifyRecipient: boolean,
    comment: string,
    notifyMailroom: boolean,
  ) => {
    await runAction({
      action,
      pieces: mailpieces,
      comment,
      notifyMailroom,
      skipNotifyRecipient,
      validationData,
      additionalData: { qrCode: qrCodeData },
    });

    dispatch(clearRecipientFilter());
  };

  return (
    <ActionWithInfo
      open={actionStarted}
      title={prefilledQrCode ? (!count ? title.single : title.plural) : 'Complete with QR Code'}
      onClose={onActionFinish}
      validationData={validationData}
      acceptButtonTitle={prefilledQrCode ? 'Complete' : 'Scan QR Code'}
      loading={isLoading}
      onAccept={
        prefilledQrCode
          ? async ({ skipNotifyRecipient, comment, notifyMailroom }) => {
              try {
                await confirmAction(prefilledQrCode, skipNotifyRecipient, comment, notifyMailroom);
                onActionFinish();
              } catch {
                dispatch(clearRecipientFilter());
              }
            }
          : undefined
      }
      infoContent={
        <Typography variant="body1" color="text.secondary">
          {prefilledQrCode ? (
            !count ? (
              prefilledInfo.single
            ) : (
              prefilledInfo.plural(mailpieces.length)
            )
          ) : count > 1 ? (
            `Please scan the recipient’s QR code for ${count} items.`
          ) : (
            'Please scan the recipient’s QR code for this item.'
          )}
        </Typography>
      }
    >
      {({
        step,
        skipNotifyRecipient,
        comment,
        notifyMailroom,
        onClose,
        onFinish,
        onBackToInfo,
      }) => (
        <DialogQrCodeConfirmation
          mailpieces={mailpieces}
          actionStarted={actionStarted && step === 'action'}
          actionRequestInProgress={isLoading}
          onActionFinish={onClose}
          onQrCodeRecognized={async (qrCodeData) => {
            try {
              await confirmAction(qrCodeData, skipNotifyRecipient, comment, notifyMailroom);
              onFinish();
            } catch {
              onBackToInfo('The scanned QR code does not match the recipient');
            }
          }}
        />
      )}
    </ActionWithInfo>
  );
};